import type { CanalEntrega, Entrega, EstadoEntrega, Hueco, MotivoIntento, Plantilla, ResultadoIntento } from '../../api/ops/entregas';
import type { EstadoChip } from '../../componentes/Chip';

/**
 * Reglas sin estilos de «Mensajes» (5.3 a 5.5). Las comprueba la API; aquí solo se reflejan para no ofrecer lo que va a
 * rechazar (antelaciones fuera de 15 min a 14 días, más de 3, repetidas; huecos más largos que su límite).
 */
export const PERMISOS_MENSAJES = {
    plantillas: 'entregas.plantillas.gestionar',
    recordatorios: 'entregas.recordatorios.configurar',
    registro: 'entregas.registro.ver',
    reenviar: 'entregas.entregas.reenviar',
    clientes: 'clientes.fichas.ver',
} as const;

// ---- 5.4 Recordatorios (G33, G44) ----

export const ANTELACION_MINIMA = 15;
export const ANTELACION_MAXIMA = 14 * 24 * 60;
export const MAXIMO_MOMENTOS = 3;

export type Unidad = 'minutos' | 'horas' | 'dias';
const MINUTOS_POR: Readonly<Record<Unidad, number>> = { minutos: 1, horas: 60, dias: 24 * 60 };

export function minutosDe(cantidad: number, unidad: Unidad): number {
    return Math.round(cantidad * MINUTOS_POR[unidad]);
}

/** null si la lista vale; si no, por qué (el texto que se enseña). */
export function errorDeAntelaciones(minutos: readonly number[]): string | null {
    if (minutos.length < 1) {
        return 'Tiene que haber al menos un momento. Si no quieres enviarlo, apágalo.';
    }
    if (minutos.length > MAXIMO_MOMENTOS) {
        return 'Como mucho, 3 momentos.';
    }
    if (minutos.some((m) => !Number.isInteger(m) || m < ANTELACION_MINIMA || m > ANTELACION_MAXIMA)) {
        return 'Cada momento, de 15 minutos a 14 días antes.';
    }
    if (new Set(minutos).size !== minutos.length) {
        return 'Ese momento ya está.';
    }
    return null;
}

/** «1 día», «2 h», «30 min», «1 día y 2 h». */
export function textoMomento(minutos: number): string {
    const dias = Math.floor(minutos / 1440);
    const horas = Math.floor((minutos % 1440) / 60);
    const min = minutos % 60;
    const partes = [dias > 0 ? `${String(dias)} ${dias === 1 ? 'día' : 'días'}` : null, horas > 0 ? `${String(horas)} h` : null, min > 0 ? `${String(min)} min` : null].filter((x): x is string => x !== null);
    if (partes.length === 0) {
        return '0 min';
    }
    return partes.length === 1 ? (partes[0] ?? '') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1] ?? ''}`;
}

/** «1 día y 2 h antes» (los momentos de mayor a menor). */
export function textoAntelaciones(minutos: readonly number[]): string {
    const orden = [...minutos].sort((a, b) => b - a).map(textoMomento);
    if (orden.length === 0) {
        return '';
    }
    const unidos = orden.length === 1 ? (orden[0] ?? '') : `${orden.slice(0, -1).join(', ')} y ${orden[orden.length - 1] ?? ''}`;
    return `${unidos} antes`;
}

// ---- 5.3 Mensajes a clientes ----

export const CANALES: Readonly<Record<CanalEntrega, string>> = { whatsapp: 'WhatsApp', instagram: 'Instagram', telegram: 'Telegram', email: 'Email' };

/** «es-ES» → «Español». */
export function nombreIdioma(locale: string): string {
    const base = locale.split('-')[0]?.toLowerCase() ?? '';
    const nombres: Readonly<Record<string, string>> = { es: 'Español', en: 'Inglés', ca: 'Catalán', fr: 'Francés', de: 'Alemán', it: 'Italiano', pt: 'Portugués' };
    return nombres[base] ?? locale;
}

/** Chip solo para «En revisión», «Rechazada» y «En pausa» (5.3). */
export function chipMeta(p: Pick<Plantilla, 'provider_status'>): readonly [string, EstadoChip] | null {
    switch (p.provider_status) {
        case 'pending':
            return ['En revisión de Meta', 'pending'];
        case 'rejected':
            return ['Rechazada por Meta', 'problem'];
        case 'paused':
            return ['En pausa en Meta', 'problem'];
        default:
            return null;
    }
}

export type Trozo = { tipo: 'texto'; texto: string } | { tipo: 'variable'; nombre: string } | { tipo: 'hueco'; clave: string };

/** Parte el texto de Reservaria en texto, datos que se rellenan solos (`{{variable}}`) y huecos (`{{slot:clave}}`). */
export function trozosDe(base: string): Trozo[] {
    const trozos: Trozo[] = [];
    const patron = /\{\{\s*(slot:)?([a-z0-9_.]+)\s*\}\}/gi;
    let desde = 0;
    for (const m of base.matchAll(patron)) {
        const i = m.index;
        if (i > desde) {
            trozos.push({ tipo: 'texto', texto: base.slice(desde, i) });
        }
        trozos.push(m[1] === undefined ? { tipo: 'variable', nombre: m[2] ?? '' } : { tipo: 'hueco', clave: m[2] ?? '' });
        desde = i + m[0].length;
    }
    if (desde < base.length) {
        trozos.push({ tipo: 'texto', texto: base.slice(desde) });
    }
    return trozos;
}

/** «cliente_nombre» → «Nombre del cliente» no lo sabe el panel: se enseña el nombre de la variable legible. */
export function nombreVariable(variable: string): string {
    const limpio = variable.replace(/^\{\{\s*|\s*\}\}$/g, '').replace(/[_.]+/g, ' ').trim();
    return limpio === '' ? variable : `${limpio.charAt(0).toLocaleUpperCase('es')}${limpio.slice(1)}`;
}

/** null si los huecos valen (obligatorios con texto y sin pasar de su límite). */
export function errorDeHuecos(huecos: readonly Hueco[], valores: Readonly<Record<string, string>>): string | null {
    for (const h of huecos) {
        const v = valores[h.key] ?? '';
        if (h.required === true && v.trim() === '') {
            return `Escribe «${h.label}».`;
        }
        if (v.length > h.max_length) {
            return `«${h.label}» admite ${String(h.max_length)} caracteres como mucho.`;
        }
    }
    return null;
}

// ---- 5.5 Enviados ----

/** Filtros de «Enviados» sin cifras (G140, G305). */
export const FILTROS_ENVIADOS = ['Todos', 'No han llegado', 'Salieron por email', 'No se enviaron'] as const;
export type FiltroEnviados = 0 | 1 | 2 | 3;

export interface ConsultaEnviados {
    status?: EstadoEntrega;
    used_fallback?: boolean;
    purpose?: 'service' | 'marketing';
    customer_id?: string;
    created_from?: string;
}

/** La consulta de `listDeliveries` de cada filtro, con «Ver las entregas» (tipo y desde cuándo) y el cliente buscado. */
export function consultaEnviados(filtro: FiltroEnviados, extra: { tipo?: 'service' | 'marketing' | null; desdeDias?: number | null; cliente?: string | null }, ahora = new Date()): ConsultaEnviados {
    const c: ConsultaEnviados = {};
    if (filtro === 1) {
        c.status = 'failed';
    } else if (filtro === 2) {
        c.used_fallback = true;
    } else if (filtro === 3) {
        c.status = 'discarded';
    }
    if (extra.tipo != null) {
        c.purpose = extra.tipo;
    }
    if (extra.cliente != null && extra.cliente !== '') {
        c.customer_id = extra.cliente;
    }
    if (extra.desdeDias != null) {
        c.created_from = new Date(ahora.getTime() - extra.desdeDias * 86_400_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
    }
    return c;
}

/** «¿Llegó?»: chip para Enviado, Por email, No ha llegado y No enviado; texto llano para Entregado y Leído (5.5). */
export const LLEGO: Readonly<Record<EstadoEntrega, readonly [string, EstadoChip | null]>> = {
    pending: ['En cola', 'pending'],
    queued: ['En cola', 'pending'],
    sent: ['Enviado', 'pending'],
    delivered: ['Entregado', null],
    read: ['Leído', null],
    fallback: ['Por email', 'neutral'],
    failed: ['No ha llegado', 'problem'],
    discarded: ['No enviado', 'neutral'],
};

export const RESULTADO: Readonly<Record<ResultadoIntento, readonly [string, EstadoChip]>> = {
    requested: ['Enviando', 'pending'],
    accepted: ['Enviado', 'pending'],
    delivered: ['Entregado', 'confirmed'],
    read: ['Leído', 'confirmed'],
    failed: ['Fallido', 'problem'],
    no_ack: ['Sin confirmar', 'problem'],
    not_sent: ['No enviado', 'neutral'],
};

export const MOTIVO: Readonly<Record<MotivoIntento, string>> = {
    channel_down: 'El canal estaba desconectado.',
    no_contact_on_channel: 'El cliente no tiene conversación en este canal.',
    outside_window_no_template: 'Fuera de las 24 horas y sin plantilla aprobada.',
    no_opt_in: 'No ha dado permiso por este canal.',
    unsubscribed: 'Se dio de baja de este canal.',
    quota_exhausted: 'Sin envíos disponibles en tu plan.',
    provider_error: 'Error del proveedor del canal.',
    hard_bounce: 'El email rebotó: no existe o no admite mensajes. En su ficha sale «Email no válido».',
    soft_bounce: 'El email rebotó: buzón lleno o servidor sin respuesta. Se reintenta solo.',
    no_ack_timeout: 'Sin confirmación de entrega a tiempo.',
    no_marketing_consent: 'Sin permiso comercial.',
};

/** Canales del reenvío del personal: «El que prefiere» (sin `channel`) o «Email» (5.5). */
export type CanalReenvio = 'preferido' | 'email';
export function cuerpoReenvio(canal: CanalReenvio): CanalEntrega | null {
    return canal === 'email' ? 'email' : null;
}

/** Ids de cliente distintos de una página, para pedir sus nombres en una sola llamada (`listCustomers` con `ids`, G303). */
export function idsDeClientes(entregas: readonly Pick<Entrega, 'customer_id'>[]): string[] {
    return [...new Set(entregas.map((e) => e.customer_id).filter((x): x is string => typeof x === 'string' && x !== ''))];
}

// ---- 5.8 Canales de envío (G562) ----

/** `entregas.canales.configurar`: Propietario y Administrador (entrada `mensajes_canales`). */
export const PERMISO_CANALES_DE_ENVIO = 'entregas.canales.configurar';
/** El saldo de WhatsApp y «Ver Plan» solo con `plan.ver` (corrección 5.1). */
export const PERMISO_PLAN_VER = 'plan.ver';

/** El texto del aviso `whatsapp_consumes_plan_messages` (G525, G550 a G552, G562), siempre junto al interruptor. */
export const AVISO_CONSUMO_WHATSAPP =
    'Cada envío por WhatsApp consume mensajes de tu paquete: primero los incluidos en tu plan y, cuando se acaban, tus paquetes extra. Si no te quedan, el mensaje sale por email.';

/** «31 de octubre» de una fecha AAAA-MM-DD (sin mover el día por la zona horaria). */
export function diaDelPeriodo(fecha: string): string {
    const [a, m, d] = fecha.slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1)).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** «Encendido por Uri Ramírez el 30 de octubre a las 11:20. Sale por WhatsApp…»; sin nombre o sin fecha, sin esa parte. */
export function textoEncendido(quien: string | null, cuando: string | null): string {
    const fecha = cuando === null ? '' : ` el ${new Date(cuando).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} a las ${new Date(cuando).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
    const autor = quien === null ? '' : ` por ${quien}`;
    return `Encendido${autor}${fecha}. Sale por WhatsApp cuando es el canal que prefiere el cliente.`;
}
